import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { refusalRedirectFor } from '../../utils/routeAccess';
import { ADMIN_OR_OPERATIONS_ROLES, hasAnyAllowedRole } from '../../utils/routeRoles';
import { useRefusalToast } from './useRefusalToast';

interface AdminOrOperationsRouteProps {
  children: React.ReactNode;
}

/**
 * AdminOrOperationsRoute component protects routes that require admin or operations role
 * Extends ProtectedRoute logic by checking for admin OR operations role
 * Redirects unauthorized users to refusalRedirectFor(roles) (/dashboard; a sales-only user to /crm/partner-enquiries) with an error message
 */
const AdminOrOperationsRoute: React.FC<AdminOrOperationsRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, loading: rolesLoading } = useCurrentUser();
  const location = useLocation();
  // Admin or operations — the same role list the sidebar reads (YOJO-3387)
  const isAuthorized = hasAnyAllowedRole(roles, ADMIN_OR_OPERATIONS_ROLES);

  // The permission toast fires from an effect, once per refusal (see useRefusalToast)
  useRefusalToast(
    'You do not have permission to access this page. Admin or Operations role required.',
    !authLoading && !rolesLoading && !!user && !isAuthorized
  );

  // Show loading spinner while checking authentication and roles
  if (authLoading || rolesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  // If authenticated but not admin or operations, redirect (dashboard; a sales-only user to Partner Enquiries) with error message
  if (!isAuthorized) {
    return <Navigate to={refusalRedirectFor(roles)} replace />;
  }

  // User is authenticated and has admin or operations role, render children
  return <>{children}</>;
};

export default AdminOrOperationsRoute;
